import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ChevronRight,
  Copy,
  Download,
  Eye,
  File as FileIcon,
  Folder,
  FolderInput,
  FolderPlus,
  FolderUp,
  Home,
  Map as MapIcon,
  Pencil,
  PenLine,
  MoreVertical,
  Share2,
  Trash2,
  Upload,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { useEditorAvailability } from "@/features/doc_editor/hooks";
import { editorActionFor } from "@/features/doc_editor/types";
import { Spinner } from "@/components/ui/Spinner";

import { twoDriveApi } from "../api";
import { triggerBrowserDownload } from "../download";
import { isGisPreviewable } from "../gisPreview";
import {
  useCopyNodes,
  useCreateFolder,
  useDeleteNode,
  useDeleteNodes,
  useMoveNodes,
  useNodeList,
  useRenameMove,
} from "../hooks";
import { DRIVE_SORT_KEYS, nodeSortRow, sortBy, useSortState } from "../sorting";
import type { DriveNode } from "../types";
import { formatBytes, formatDateTime } from "../utils";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu";
import { FolderPickerDialog } from "./FolderPickerDialog";
import { GisPreviewScreen } from "./GisPreviewScreen";
import { NameDialog } from "./NameDialog";
import { ShareDialog } from "./ShareDialog";
import { SortHeader } from "./SortHeader";
import { UploadDialog, type UploadMode } from "./UploadDialog";

// Custom drag MIME so an internal node-drag is never confused with an OS file drop.
const DRAG_TYPE = "application/x-twodrive-nodes";

/**
 * The listing grid — the header row and every item row MUST use this one class,
 * or the columns drift apart: each <li> is its own grid, so no column except the
 * name may be `auto`-sized. Columns, in DOM order: selection, name, uploader,
 * created, modified, size, actions. Narrow screens drop the middle columns (their
 * values move under the name) and the widest one appears only from `xl`.
 */
const ROW_GRID =
  "grid items-center gap-3 grid-cols-[1.25rem_minmax(0,1fr)_2rem]" +
  " sm:grid-cols-[1.25rem_minmax(0,1fr)_5rem_9.5rem]" +
  " lg:grid-cols-[1.25rem_minmax(0,1fr)_9rem_11rem_5rem_9.5rem]" +
  " xl:grid-cols-[1.25rem_minmax(0,1fr)_9rem_9rem_11rem_5rem_9.5rem]";

const CELL_TEXT = "text-xs text-[var(--color-text-muted)]";

interface Props {
  parent: string | null;
  rootLabel: string;
  onNavigate: (id: string | null) => void;
  onHome?: () => void;
  /** Zip a single folder (existing per-folder download). */
  onZip: (node: DriveNode) => void;
  /** Zip an arbitrary selection of node ids into one archive. */
  onZipSelection: (ids: string[], label: string) => void;
}

export function FolderBrowser({
  parent,
  rootLabel,
  onNavigate,
  onHome,
  onZip,
  onZipSelection,
}: Props) {
  const { data, isLoading } = useNodeList(parent);
  const [sort, toggleSort] = useSortState(DRIVE_SORT_KEYS);
  const nodes = useMemo(() => sortBy(data?.nodes ?? [], nodeSortRow, sort), [data?.nodes, sort]);
  const meta = data?.meta;
  const canWrite = meta?.my_access === "owner" || meta?.my_access === "edit";
  const isOwner = meta?.my_access === "owner";

  const createFolder = useCreateFolder(parent);
  const renameMove = useRenameMove(parent);
  const deleteNode = useDeleteNode(parent);
  const deleteNodes = useDeleteNodes(parent);
  const moveNodes = useMoveNodes();
  const copyNodes = useCopyNodes();

  const [uploadMode, setUploadMode] = useState<UploadMode | null>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<DriveNode | null>(null);
  const [shareTarget, setShareTarget] = useState<DriveNode | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [menu, setMenu] = useState<{ x: number; y: number; node: DriveNode } | null>(null);
  const [picker, setPicker] = useState<{ mode: "move" | "copy"; ids: string[] } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const dragIds = useRef<string[]>([]);

  // Selection is scoped to one folder view — reset it whenever we navigate.
  useEffect(() => {
    setSelected(new Set());
  }, [parent]);

  const breadcrumb = meta?.breadcrumb ?? [];
  const allSelected = nodes.length > 0 && nodes.every((n) => selected.has(n.id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(nodes.map((n) => n.id)));
  }
  const clearSelection = () => setSelected(new Set());

  async function downloadFile(node: DriveNode) {
    const res = await twoDriveApi.download(node.id);
    if (res.url) triggerBrowserDownload(res.url, res.filename ?? node.name);
  }

  // Edytor w przeglądarce (Collabora na pełnym ekranie): plik, którego format edytor zna,
  // otwiera się po kliknięciu w nazwę zamiast od razu pobierać. Zapis nadpisuje plik
  // na dysku; formaty tylko-do-podglądu (PDF, obrazy) i udostępnienia „view" otwierają
  // się w trybie odczytu. Pobranie zostaje pod ikoną i w menu.
  const qc = useQueryClient();
  const { data: editorAvail } = useEditorAvailability();
  const editorExt = editorAvail?.available ? editorAvail.extensions ?? null : null;
  const [editorNode, setEditorNode] = useState<DriveNode | null>(null);
  const editorAction = (node: DriveNode) =>
    node.node_type === "file" && node.status === "active" ? editorActionFor(node.name, editorExt) : null;
  // GeoPackage opens on a read-only map rendered by QGIS Server (no Collabora involved).
  const [gisNode, setGisNode] = useState<DriveNode | null>(null);
  const hasGisPreview = (node: DriveNode) =>
    node.node_type === "file" && node.status === "active" && isGisPreviewable(node.name);

  function onRowNameClick(node: DriveNode) {
    if (node.node_type === "folder") onNavigate(node.id);
    else if (hasGisPreview(node)) setGisNode(node);
    else if (editorAction(node)) setEditorNode(node);
    else downloadFile(node);
  }

  function confirmDelete(node: DriveNode) {
    if (
      window.confirm(
        `Usunąć „${node.name}"${node.node_type === "folder" ? " wraz z zawartością" : ""}?`,
      )
    ) {
      deleteNode.mutate(node.id);
    }
  }

  function deleteSelection(ids: string[]) {
    if (window.confirm(`Usunąć zaznaczone elementy (${ids.length})?`)) {
      deleteNodes.mutate(ids, { onSuccess: clearSelection });
    }
  }

  // ── drag & drop (move) ───────────────────────────────────────────────────
  function onRowDragStart(e: React.DragEvent, node: DriveNode) {
    // Drag the whole selection when the grabbed row is part of it; else just it.
    const ids = selected.has(node.id) && selected.size > 0 ? [...selected] : [node.id];
    dragIds.current = ids;
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(ids));
    e.dataTransfer.effectAllowed = "move";
  }
  function onRowDragEnd() {
    dragIds.current = [];
    setDropTarget(null);
  }
  function isInternalDrag(e: React.DragEvent) {
    return e.dataTransfer.types.includes(DRAG_TYPE);
  }
  function allowDropOn(key: string, valid: boolean) {
    return (e: React.DragEvent) => {
      if (!isInternalDrag(e) || !valid) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDropTarget(key);
    };
  }
  // Live check (ref, not render-time state): a folder can't receive the very
  // nodes being dragged.
  function onFolderDragOver(e: React.DragEvent, node: DriveNode) {
    if (node.node_type !== "folder" || dragIds.current.includes(node.id)) return;
    allowDropOn(node.id, true)(e);
  }
  function onFolderDrop(node: DriveNode) {
    if (node.node_type !== "folder" || dragIds.current.includes(node.id)) {
      setDropTarget(null);
      return;
    }
    doDrop(node.id);
  }
  function doDrop(dest: string | null) {
    const ids = dragIds.current;
    setDropTarget(null);
    if (!ids.length) return;
    moveNodes.mutate(
      { ids, dest },
      {
        onSuccess: clearSelection,
        onError: (err) => window.alert(errorMessage(err, "Nie udało się przenieść.")),
      },
    );
  }

  // ── context menu ─────────────────────────────────────────────────────────
  function openMenu(e: React.MouseEvent, node: DriveNode) {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, node });
  }

  function menuItems(node: DriveNode): ContextMenuItem[] {
    // Right-clicking a selected row acts on the whole selection.
    const onSelection = selected.has(node.id) && selected.size > 1;
    const ids = onSelection ? [...selected] : [node.id];
    const items: ContextMenuItem[] = [];

    if (onSelection) {
      items.push({
        label: `Pobierz jako ZIP (${ids.length})`,
        icon: <Archive size={15} />,
        onClick: () => onZipSelection(ids, `Zaznaczone (${ids.length})`),
      });
    } else if (node.node_type === "folder") {
      items.push({ label: "Otwórz", icon: <Folder size={15} />, onClick: () => onNavigate(node.id) });
      items.push({ label: "Pobierz jako ZIP", icon: <Archive size={15} />, onClick: () => onZip(node) });
    } else {
      if (hasGisPreview(node)) {
        items.push({ label: "Podgląd mapy", icon: <MapIcon size={15} />, onClick: () => setGisNode(node) });
      }
      const action = editorAction(node);
      if (action) {
        items.push({
          label: action === "edit" && canWrite ? "Otwórz w edytorze" : "Podgląd",
          icon: action === "edit" && canWrite ? <PenLine size={15} /> : <Eye size={15} />,
          onClick: () => setEditorNode(node),
        });
      }
      items.push({ label: "Pobierz", icon: <Download size={15} />, onClick: () => downloadFile(node) });
    }

    items.push({
      label: "Kopiuj",
      icon: <Copy size={15} />,
      onClick: () => setPicker({ mode: "copy", ids }),
    });

    if (canWrite) {
      items.push({
        label: "Przenieś",
        icon: <FolderInput size={15} />,
        onClick: () => setPicker({ mode: "move", ids }),
      });
      if (!onSelection) {
        items.push({
          label: "Zmień nazwę",
          icon: <Pencil size={15} />,
          onClick: () => setRenameTarget(node),
        });
      }
    }

    if (isOwner && !onSelection) {
      items.push({
        label: "Udostępnij",
        icon: <Share2 size={15} />,
        onClick: () => setShareTarget(node),
      });
    }

    if (canWrite) {
      items.push({
        label: onSelection ? `Usuń (${ids.length})` : "Usuń",
        icon: <Trash2 size={15} />,
        danger: true,
        separator: true,
        onClick: () => (onSelection ? deleteSelection(ids) : confirmDelete(node)),
      });
    }
    return items;
  }

  function onPickerConfirm(dest: string | null) {
    if (!picker) return;
    const { mode, ids } = picker;
    const mutation = mode === "copy" ? copyNodes : moveNodes;
    mutation.mutate(
      { ids, dest },
      {
        onSuccess: () => {
          setPicker(null);
          clearSelection();
        },
        onError: (err) =>
          window.alert(
            errorMessage(err, mode === "copy" ? "Nie udało się skopiować." : "Nie udało się przenieść."),
          ),
      },
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      {canWrite && (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setNewFolderOpen(true)}>
            <FolderPlus size={14} /> Nowy folder
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setUploadMode("folder")}>
            <FolderUp size={14} /> Prześlij folder
          </Button>
          <Button size="sm" onClick={() => setUploadMode("files")}>
            <Upload size={14} /> Wgraj pliki
          </Button>
        </div>
      )}

      <nav className="flex flex-wrap items-center gap-1 text-sm font-mono text-[var(--color-text-secondary)] min-w-0">
        <button
          onClick={() => (onHome ? onHome() : onNavigate(null))}
          onDragOver={allowDropOn("__home__", !onHome)}
          onDragLeave={() => setDropTarget(null)}
          onDrop={() => doDrop(null)}
          className={`inline-flex items-center gap-1 hover:text-[var(--color-primary)] ${
            dropTarget === "__home__" ? "text-[var(--color-primary)] font-bold" : ""
          }`}
        >
          <Home size={14} /> {rootLabel}
        </button>
        {breadcrumb.map((c, i) => (
          <span key={c.id} className="inline-flex items-center gap-1 min-w-0">
            <ChevronRight size={13} className="text-[var(--color-text-muted)]" />
            {i === breadcrumb.length - 1 ? (
              <span className="text-[var(--color-text-primary)] truncate">{c.name}</span>
            ) : (
              <button
                onClick={() => onNavigate(c.id)}
                onDragOver={allowDropOn(c.id, true)}
                onDragLeave={() => setDropTarget(null)}
                onDrop={() => doDrop(c.id)}
                className={`hover:text-[var(--color-primary)] truncate ${
                  dropTarget === c.id ? "text-[var(--color-primary)] font-bold" : ""
                }`}
              >
                {c.name}
              </button>
            )}
          </span>
        ))}
      </nav>

      {/* Selection action bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-[var(--color-bg-base)] border border-[var(--color-border)] text-sm">
          <span className="font-mono text-[var(--color-text-secondary)]">
            Zaznaczono: {selected.size}
          </span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onZipSelection([...selected], `Zaznaczone (${selected.size})`)}
          >
            <Archive size={14} /> Pobierz jako ZIP
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setPicker({ mode: "copy", ids: [...selected] })}>
            <Copy size={14} /> Kopiuj
          </Button>
          {canWrite && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setPicker({ mode: "move", ids: [...selected] })}
              >
                <FolderInput size={14} /> Przenieś
              </Button>
              <Button size="sm" variant="danger" onClick={() => deleteSelection([...selected])}>
                <Trash2 size={14} /> Usuń
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={clearSelection}>
            Wyczyść
          </Button>
        </div>
      )}

      {/* File/folder list */}
      {isLoading ? (
        <div className="py-10 flex justify-center">
          <Spinner />
        </div>
      ) : nodes.length === 0 ? (
        <div className="py-10 text-center text-sm text-[var(--color-text-muted)]">
          Ten folder jest pusty.
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-border)] border border-[var(--color-border)]">
          {/* Header: select-all + sortable columns */}
          <li className={`${ROW_GRID} group px-3 py-1.5 bg-[var(--color-bg-base)]`}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="cursor-pointer accent-[var(--color-primary)]"
              title="Zaznacz wszystko"
            />
            <SortHeader label="Nazwa" sortKey="name" sort={sort} onSort={toggleSort} />
            <SortHeader
              label="Dodał/a"
              sortKey="uploader"
              sort={sort}
              onSort={toggleSort}
              className="hidden lg:inline-flex"
            />
            <SortHeader
              label="Utworzono"
              sortKey="created"
              sort={sort}
              onSort={toggleSort}
              className="hidden xl:inline-flex"
            />
            <SortHeader
              label="Ostatnia modyfikacja"
              sortKey="modified"
              sort={sort}
              onSort={toggleSort}
              className="hidden lg:inline-flex"
            />
            <SortHeader
              label="Rozmiar"
              sortKey="size"
              sort={sort}
              onSort={toggleSort}
              className="hidden sm:inline-flex justify-end"
            />
          </li>

          {nodes.map((node) => {
            const isFolder = node.node_type === "folder";
            const uploader = node.uploaded_by_name || "—";
            return (
              <li
                key={node.id}
                draggable
                onDragStart={(e) => onRowDragStart(e, node)}
                onDragEnd={onRowDragEnd}
                onDragOver={(e) => onFolderDragOver(e, node)}
                onDragLeave={() => setDropTarget(null)}
                onDrop={() => onFolderDrop(node)}
                onContextMenu={(e) => openMenu(e, node)}
                className={`${ROW_GRID} px-3 py-2 group ${
                  dropTarget === node.id
                    ? "bg-[var(--color-primary)]/10 ring-1 ring-inset ring-[var(--color-primary)]"
                    : selected.has(node.id)
                      ? "bg-[var(--color-primary)]/5"
                      : "hover:bg-[var(--color-bg-base)]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(node.id)}
                  onChange={() => toggle(node.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="cursor-pointer accent-[var(--color-primary)]"
                />
                <button
                  onClick={() => onRowNameClick(node)}
                  className="flex items-center gap-2 min-w-0 text-left"
                >
                  {isFolder ? (
                    <Folder size={16} className="text-blue-500 flex-shrink-0" />
                  ) : (
                    <FileIcon size={16} className="text-[var(--color-text-muted)] flex-shrink-0" />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate text-sm">{node.name}</span>
                    {/* Wąski ekran: dane z ukrytych kolumn schodzą pod nazwę. */}
                    <span className="block text-[11px] leading-tight text-[var(--color-text-muted)] lg:hidden">
                      {!isFolder && <span className="sm:hidden">{formatBytes(node.size_bytes)} · </span>}
                      {uploader} · zm. {formatDateTime(node.updated_at)}
                    </span>
                  </span>
                </button>

                <span className={`hidden lg:block truncate ${CELL_TEXT}`} title={uploader}>
                  {uploader}
                </span>
                <span className={`hidden xl:block truncate font-mono ${CELL_TEXT}`}>
                  {formatDateTime(node.created_at)}
                </span>
                <span className={`hidden lg:block truncate font-mono ${CELL_TEXT}`}>
                  {formatDateTime(node.updated_at)}
                </span>
                <span className={`hidden sm:block text-right font-mono ${CELL_TEXT}`}>
                  {isFolder ? "" : formatBytes(node.size_bytes)}
                </span>

                <div className="flex items-center justify-end gap-1">
                  {/* Telefon: komplet akcji siedzi w menu kontekstowym — pięć ikon
                      zjadałoby szerokość potrzebną nazwie i danym pod nią. */}
                  <button
                    onClick={(e) => openMenu(e, node)}
                    className="sm:hidden p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                    title="Więcej"
                  >
                    <MoreVertical size={15} />
                  </button>
                  <div className="hidden sm:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {hasGisPreview(node) && (
                    <button
                      onClick={() => setGisNode(node)}
                      className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                      title="Podgląd mapy"
                    >
                      <MapIcon size={15} />
                    </button>
                  )}
                  {!isFolder && editorAction(node) && (
                    <button
                      onClick={() => setEditorNode(node)}
                      className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                      title={editorAction(node) === "edit" && canWrite ? "Otwórz w edytorze" : "Podgląd"}
                    >
                      {editorAction(node) === "edit" && canWrite ? <PenLine size={15} /> : <Eye size={15} />}
                    </button>
                  )}
                  <button
                    onClick={() => (isFolder ? onZip(node) : downloadFile(node))}
                    className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                    title="Pobierz"
                  >
                    <Download size={15} />
                  </button>
                  {isOwner && (
                    <button
                      onClick={() => setShareTarget(node)}
                      className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                      title="Udostępnij"
                    >
                      <Share2 size={15} />
                    </button>
                  )}
                  {canWrite && (
                    <>
                      <button
                        onClick={() => setRenameTarget(node)}
                        className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        title="Zmień nazwę"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => confirmDelete(node)}
                        className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        title="Usuń"
                      >
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editorNode && (
        <OdtEditorScreen
          kind="drive_file"
          id={editorNode.id}
          title={editorNode.name}
          onClose={() => {
            setEditorNode(null);
            // Zapis podmienił plik węzła (rozmiar, data) i ruszył limit właściciela.
            qc.invalidateQueries({ queryKey: ["two-drive", "nodes"] });
            qc.invalidateQueries({ queryKey: ["two-drive", "quota"] });
          }}
        />
      )}
      {gisNode && (
        <GisPreviewScreen
          nodeId={gisNode.id}
          name={gisNode.name}
          onClose={() => setGisNode(null)}
          onDownload={() => downloadFile(gisNode)}
        />
      )}
      {menu && (
        <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.node)} onClose={() => setMenu(null)} />
      )}

      <FolderPickerDialog
        open={picker !== null}
        title={picker?.mode === "copy" ? "Kopiuj do…" : "Przenieś do…"}
        confirmLabel={picker?.mode === "copy" ? "Kopiuj tutaj" : "Przenieś tutaj"}
        submitting={copyNodes.isPending || moveNodes.isPending}
        excludeIds={picker ? new Set(picker.ids) : undefined}
        onClose={() => setPicker(null)}
        onConfirm={onPickerConfirm}
      />

      <NameDialog
        open={newFolderOpen}
        title="Nowy folder"
        label="Nazwa folderu"
        submitting={createFolder.isPending}
        onClose={() => setNewFolderOpen(false)}
        onSubmit={(name) => createFolder.mutate(name, { onSuccess: () => setNewFolderOpen(false) })}
      />

      <NameDialog
        open={!!renameTarget}
        title="Zmień nazwę"
        label="Nowa nazwa"
        initialValue={renameTarget?.name}
        submitting={renameMove.isPending}
        onClose={() => setRenameTarget(null)}
        onSubmit={(name) =>
          renameTarget &&
          renameMove.mutate({ id: renameTarget.id, name }, { onSuccess: () => setRenameTarget(null) })
        }
      />

      <ShareDialog node={shareTarget} onClose={() => setShareTarget(null)} />

      <UploadDialog
        parent={parent}
        open={uploadMode !== null}
        mode={uploadMode ?? "files"}
        onClose={() => setUploadMode(null)}
      />
    </div>
  );
}

/**
 * Best-effort extraction of a backend error message. The API envelope is
 * { data, errors, meta } where `errors` carries DRF's payload — a string list
 * for non-field errors or a { detail } object for permission/not-found errors.
 */
function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { errors?: unknown } } };
  const errors = e?.response?.data?.errors;
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors) && typeof errors[0] === "string") return errors[0];
  if (errors && typeof errors === "object") {
    const detail = (errors as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}
